import { useState } from "react";
import { Linkedin, Twitter, Facebook, Mail, Link2, Check } from "lucide-react";

/**
 * Social sharing buttons — LinkedIn, X/Twitter, Facebook, Email, Copy link.
 * Renders as a compact vertical stack in the article sidebar.
 */
const ArticleShare = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title);

  const links = [
    {
      icon: Linkedin,
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      testId: "share-linkedin",
    },
    {
      icon: Twitter,
      label: "Share on X (Twitter)",
      href: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encTitle}`,
      testId: "share-twitter",
    },
    {
      icon: Facebook,
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      testId: "share-facebook",
    },
    {
      icon: Mail,
      label: "Share via email",
      href: `mailto:?subject=${encTitle}&body=${encUrl}`,
      testId: "share-email",
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard not available */
    }
  };

  return (
    <div data-testid="article-share">
      <p className="mono" style={{ fontSize: ".68rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 1rem" }}>
        Share this article
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".55rem" }}>
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <a
              key={l.testId}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={l.label}
              data-testid={l.testId}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "38px",
                height: "38px",
                border: "1px solid var(--line)",
                color: "var(--ink-soft)",
                transition: "border-color .25s, color .25s, background .25s",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--brass-deep)";
                e.currentTarget.style.color = "var(--brass-deep)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--line)";
                e.currentTarget.style.color = "var(--ink-soft)";
              }}
            >
              <Icon size={16} strokeWidth={1.6} aria-hidden="true" />
            </a>
          );
        })}
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? "Link copied" : "Copy article link"}
          data-testid="share-copy"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "38px",
            height: "38px",
            border: "1px solid var(--line)",
            background: "transparent",
            color: copied ? "var(--brass-deep)" : "var(--ink-soft)",
            cursor: "pointer",
            padding: 0,
            transition: "border-color .25s, color .25s",
          }}
        >
          {copied ? <Check size={16} strokeWidth={1.8} /> : <Link2 size={16} strokeWidth={1.6} />}
        </button>
      </div>
    </div>
  );
};

export default ArticleShare;
